import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal, Input, Select } from '@/components/common';
import { api } from '@/services/api';
import { platformMdas, type MDA } from '@/services/platform';
import styles from './PlatformUsersPage.module.css';

interface PlatformUser {
  id: string;
  mdaId: string;
  email: string;
  firstName: string;
  lastName: string;
  employeeId: string | null;
  departmentId: string | null;
  jobTitle: string | null;
  phone: string | null;
  securityClearance: number;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  mda: {
    code: string;
    name: string;
  };
  department: { name: string } | null;
}

interface ApiMeta {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

// Custom hook for debounced value
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function PlatformUsersPage() {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [mdas, setMdas] = useState<MDA[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<ApiMeta | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedMda, setSelectedMda] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // Debounced search value (300ms delay)
  const debouncedSearch = useDebounce(search, 300);

  // Track if this is the initial load
  const isInitialMount = useRef(true);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<PlatformUser | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    mdaId: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    employeeId: '',
    jobTitle: '',
    phone: '',
    securityClearance: '1',
  });

  useEffect(() => {
    fetchMdas();
  }, []);

  // Fetch users when filters change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchUsers();
      return;
    }
    fetchUsers();
  }, [currentPage, selectedMda, statusFilter, perPage, debouncedSearch]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (!isInitialMount.current && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedSearch, selectedMda, statusFilter, perPage]);

  async function fetchMdas() {
    try {
      const response = await platformMdas.list();
      if (response.success && response.data) {
        setMdas(response.data);
      }
    } catch {
      // Ignore MDA fetch errors
    }
  }

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number> = {
        page: currentPage,
        perPage: perPage,
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (selectedMda) params.mdaId = selectedMda;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active' ? 'true' : 'false';

      const response = await api.get<PlatformUser[]>('/platform/users', params);

      if (response.success && response.data) {
        setUsers(response.data);
        if (response.meta) {
          setMeta(response.meta as ApiMeta);
        }
      } else {
        setError(response.error?.message || 'Failed to load users');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, debouncedSearch, selectedMda, statusFilter]);

  function openCreateModal() {
    setEditingUser(null);
    setFormData({
      mdaId: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      employeeId: '',
      jobTitle: '',
      phone: '',
      securityClearance: '1',
    });
    setIsModalOpen(true);
  }

  function openEditModal(user: PlatformUser) {
    setEditingUser(user);
    setFormData({
      mdaId: user.mdaId,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      password: '',
      employeeId: user.employeeId || '',
      jobTitle: user.jobTitle || '',
      phone: user.phone || '',
      securityClearance: String(user.securityClearance),
    });
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setEditingUser(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (editingUser) {
        const updateData: Record<string, unknown> = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          employeeId: formData.employeeId || null,
          jobTitle: formData.jobTitle || null,
          phone: formData.phone || null,
          securityClearance: parseInt(formData.securityClearance),
        };

        if (formData.password) {
          updateData.password = formData.password;
        }

        const response = await api.put(`/platform/users/${editingUser.id}`, updateData);

        if (response.success) {
          await fetchUsers();
          closeModal();
        } else {
          setError(response.error?.message || 'Failed to update user');
        }
      } else {
        const response = await api.post('/platform/users', {
          mdaId: formData.mdaId,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          password: formData.password,
          employeeId: formData.employeeId || undefined,
          jobTitle: formData.jobTitle || undefined,
          phone: formData.phone || undefined,
          securityClearance: parseInt(formData.securityClearance),
        });

        if (response.success) {
          await fetchUsers();
          closeModal();
        } else {
          setError(response.error?.message || 'Failed to create user');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleUserStatus(user: PlatformUser) {
    try {
      const response = await api.put(`/platform/users/${user.id}`, {
        isActive: !user.isActive,
      });

      if (response.success) {
        await fetchUsers();
      } else {
        setError(response.error?.message || 'Failed to update user status');
      }
    } catch {
      setError('Failed to update user status');
    }
  }

  function formatDate(dateStr: string | null) {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  // Generate page numbers for pagination
  function getPageNumbers(): (number | 'ellipsis')[] {
    if (!meta) return [];

    const totalPages = meta.totalPages;
    const current = currentPage;
    const pages: (number | 'ellipsis')[] = [];

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (current > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current
      const start = Math.max(2, current - 1);
      const end = Math.min(totalPages - 1, current + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (current < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }

  const activeUsers = users.filter(u => u.isActive).length;
  const totalUsers = meta?.total || users.length;
  const startRecord = meta ? ((currentPage - 1) * perPage) + 1 : 0;
  const endRecord = meta ? Math.min(currentPage * perPage, meta.total) : users.length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Users Management</h1>
          <p className={styles.subtitle}>Manage all users across institutions</p>
        </div>
        <div className={styles.headerStats}>
          <div className={styles.headerStat}>
            <span className={styles.headerStatValue}>{totalUsers}</span>
            <span className={styles.headerStatLabel}>Total Users</span>
          </div>
          <div className={styles.headerStat}>
            <span className={styles.headerStatValue}>{activeUsers}</span>
            <span className={styles.headerStatLabel}>Active</span>
          </div>
        </div>
      </div>

      <Button onClick={openCreateModal} style={{ marginBottom: 'var(--spacing-xl)' }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        Add New User
      </Button>

      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
          <button onClick={() => setError(null)} className={styles.errorClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <svg className={styles.searchIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, or staff ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
          {search && (
            <button
              className={styles.searchClear}
              onClick={() => setSearch('')}
              type="button"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          {loading && search && (
            <div className={styles.searchSpinner} />
          )}
        </div>

        <div className={styles.filterGroup}>
          <Select
            value={selectedMda}
            onChange={(e) => setSelectedMda(e.target.value)}
          >
            <option value="">All MDAs</option>
            {mdas.map((mda) => (
              <option key={mda.id} value={mda.id}>{mda.code} - {mda.name}</option>
            ))}
          </Select>

          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </div>
      </div>

      {loading && !search ? (
        <div className={styles.loading}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>Loading users...</span>
        </div>
      ) : users.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h3 className={styles.emptyTitle}>No users found</h3>
          <p className={styles.emptyDescription}>
            {search || selectedMda || statusFilter !== 'all'
              ? 'Try adjusting your filters to find users'
              : 'Get started by adding your first user'}
          </p>
          {!search && !selectedMda && statusFilter === 'all' && (
            <Button onClick={openCreateModal}>Add First User</Button>
          )}
        </div>
      ) : (
        <>
          <div className={styles.table}>
            <div className={styles.tableHeader}>
              <div className={styles.tableCell}>User</div>
              <div className={styles.tableCell}>MDA</div>
              <div className={styles.tableCell}>Staff ID</div>
              <div className={styles.tableCell}>Job Title</div>
              <div className={styles.tableCell}>Last Login</div>
              <div className={styles.tableCell}>Status</div>
              <div className={styles.tableCell}>Actions</div>
            </div>
            <div className={styles.tableBody}>
              {users.map((user, index) => (
                <div
                  key={user.id}
                  className={styles.tableRow}
                  style={{ animationDelay: `${0.05 * (index + 1)}s` }}
                >
                  <div className={styles.tableCell}>
                    <div className={styles.userInfo}>
                      <div className={styles.avatar}>
                        {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                      </div>
                      <div className={styles.userDetails}>
                        <span className={styles.userName}>{user.firstName} {user.lastName}</span>
                        <span className={styles.userEmail}>{user.email}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.tableCell}>
                    <span className={styles.mdaCode}>{user.mda.code}</span>
                  </div>
                  <div className={styles.tableCell}>
                    {user.employeeId || '—'}
                  </div>
                  <div className={styles.tableCell}>
                    {user.jobTitle || '—'}
                  </div>
                  <div className={styles.tableCell}>
                    {formatDate(user.lastLoginAt)}
                  </div>
                  <div className={styles.tableCell}>
                    <span className={`${styles.status} ${user.isActive ? styles.active : styles.inactive}`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className={styles.tableCell}>
                    <div className={styles.actions}>
                      <button
                        className={`${styles.actionButton} ${styles.edit}`}
                        title="Edit user"
                        onClick={() => openEditModal(user)}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.388 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button
                        className={`${styles.actionButton} ${user.isActive ? styles.deactivate : styles.activate}`}
                        title={user.isActive ? 'Deactivate user' : 'Activate user'}
                        onClick={() => toggleUserStatus(user)}
                      >
                        {user.isActive ? (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M10 6L6 10M6 6L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M6 8L7.5 9.5L10 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {meta && (
              <div className={styles.pagination}>
                <div className={styles.paginationLeft}>
                  <span className={styles.pageInfo}>
                    Showing <strong>{startRecord}</strong> to <strong>{endRecord}</strong> of <strong>{meta.total}</strong> users
                  </span>
                  <div className={styles.perPageSelect}>
                    <span>Show</span>
                    <select
                      value={perPage}
                      onChange={(e) => setPerPage(Number(e.target.value))}
                      className={styles.perPageDropdown}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>per page</span>
                  </div>
                </div>

                {meta.totalPages > 1 && (
                  <div className={styles.pageControls}>
                    <button
                      className={styles.pageButton}
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                      title="First page"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11 12L7 8L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M6 12V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                    <button
                      className={styles.pageButton}
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      title="Previous page"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>

                    <div className={styles.pageNumbers}>
                      {getPageNumbers().map((page, index) =>
                        page === 'ellipsis' ? (
                          <span key={`ellipsis-${index}`} className={styles.pageEllipsis}>...</span>
                        ) : (
                          <button
                            key={page}
                            className={`${styles.pageNumber} ${currentPage === page ? styles.active : ''}`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      className={styles.pageButton}
                      disabled={currentPage === meta.totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      title="Next page"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      className={styles.pageButton}
                      disabled={currentPage === meta.totalPages}
                      onClick={() => setCurrentPage(meta.totalPages)}
                      title="Last page"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M5 4L9 8L5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 4V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingUser ? 'Edit User' : 'Add New User'}
      >
        <form className={styles.form} onSubmit={handleSubmit}>
          {!editingUser && (
            <Select
              label="Institution (MDA)"
              value={formData.mdaId}
              onChange={(e) => setFormData({ ...formData, mdaId: e.target.value })}
              required
            >
              <option value="">Select an institution</option>
              {mdas.filter(m => m.isActive).map((mda) => (
                <option key={mda.id} value={mda.id}>{mda.code} - {mda.name}</option>
              ))}
            </Select>
          )}

          <Input
            label="Email Address"
            type="email"
            placeholder="user@institution.gov.gh"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            disabled={!!editingUser}
          />

          <div className={styles.formRow}>
            <Input
              label="First Name"
              placeholder="Kwame"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
            <Input
              label="Last Name"
              placeholder="Asante"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </div>

          <Input
            label={editingUser ? 'New Password (leave empty to keep current)' : 'Password'}
            type="password"
            placeholder="••••••••"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required={!editingUser}
          />

          <div className={styles.formRow}>
            <Input
              label="Staff ID"
              placeholder="GHS-001"
              value={formData.employeeId}
              onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
            />
            <Input
              label="Job Title"
              placeholder="Records Officer"
              value={formData.jobTitle}
              onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
            />
          </div>

          <div className={styles.formRow}>
            <Input
              label="Phone Number"
              placeholder="+233 XX XXX XXXX"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            <Select
              label="Security Clearance"
              value={formData.securityClearance}
              onChange={(e) => setFormData({ ...formData, securityClearance: e.target.value })}
            >
              <option value="1">Level 1 - Basic</option>
              <option value="2">Level 2 - Standard</option>
              <option value="3">Level 3 - Elevated</option>
              <option value="4">Level 4 - High</option>
              <option value="5">Level 5 - Top Secret</option>
            </Select>
          </div>

          <div className={styles.modalActions}>
            <Button variant="secondary" type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
